if (!customElements.get("lookbook-gallery")) {
  class LookbookGallery extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        window.addEventListener("shopify:section:load", this.init.bind(this));
      }
      // Pre-measure on click so pswp's first paddingFn call returns the correct
      // value and the image opens at the right size with no resize transition.
      this.addEventListener("click", this.preMeasureOnItemClick.bind(this), true);
      this.init();
      this.subscribeToCartUpdates();
    }

    preMeasureOnItemClick(e) {
      const link = e.target.closest("a.lookbook-item__link");
      if (!link) return;
      this.computeMaxReservedBottom();
    }

    computeMaxReservedBottom() {
      if (this.reservedBottom != null) return;
      if (window.innerWidth >= 1024) {
        this.reservedBottom = 0;
        return;
      }
      const links = this.querySelectorAll("a.lookbook-item__link");
      const measureEl = document.createElement("div");
      measureEl.className = "pswp__lookbook-products";
      measureEl.style.position = "fixed";
      measureEl.style.insetInlineStart = "max(1.6rem, env(safe-area-inset-left))";
      measureEl.style.insetInlineEnd = "max(1.6rem, env(safe-area-inset-right))";
      // Mobile media query sets inset-block-start to anchor the live container
      // at the top of the reserved band; override here so the measure div takes
      // its intrinsic height instead of stretching to fill 40vh.
      measureEl.style.insetBlockStart = "auto";
      measureEl.style.insetBlockEnd = "0";
      measureEl.style.visibility = "hidden";
      measureEl.style.pointerEvents = "none";
      document.body.appendChild(measureEl);
      let maxHeight = 0;
      links.forEach((link) => {
        const itemId = link.dataset.itemId;
        const tpl = itemId && document.getElementById(`lookbook-products-${itemId}`);
        if (!tpl || !tpl.innerHTML.trim()) return;
        measureEl.innerHTML = tpl.innerHTML;
        if (measureEl.offsetHeight > maxHeight) maxHeight = measureEl.offsetHeight;
      });
      document.body.removeChild(measureEl);
      this.reservedBottom = maxHeight > 0 ? maxHeight + 16 : 0;
    }

    init() {
      if (typeof PhotoSwipeLightbox === "undefined") {
        console.warn("[lookbook-gallery] PhotoSwipe not loaded");
        return;
      }
      if (this.lightbox) {
        try {
          this.lightbox.destroy();
        } catch (e) {}
      }
      this.initPhotoSwipe();
    }

    initPhotoSwipe() {
      const closeIcon = document.querySelector("[data-lookbook-close-icon]");
      const prevIcon = document.querySelector("[data-lookbook-prev-icon]");
      const nextIcon = document.querySelector("[data-lookbook-next-icon]");

      this.lightbox = new PhotoSwipeLightbox({
        gallery: this,
        children: "a.lookbook-item__link",
        pswpModule: PhotoSwipe,
        mainClass: "pswp--lookbook",
        bgOpacity: 1,
        loop: false,
        showHideAnimationType: "none",
        showAnimationDuration: 0,
        hideAnimationDuration: 0,
        paddingFn: (viewportSize) =>
          viewportSize.x >= 1024
            ? { top: 0, right: 0, bottom: 0, left: 0 }
            : { top: 0, right: 0, bottom: this.reservedBottom || 0, left: 0 },
        initialZoomLevel: (zl) => (window.innerWidth < 750 ? zl.vFill : zl.fit),
        secondaryZoomLevel: (zl) => (window.innerWidth < 750 ? zl.fit : 1)
      });

      this.lightbox.addFilter("uiElement", (el, data) => {
        if (data.name === "close" && closeIcon) el.innerHTML = closeIcon.innerHTML;
        else if (data.name === "arrowPrev" && prevIcon) el.innerHTML = prevIcon.innerHTML;
        else if (data.name === "arrowNext" && nextIcon) el.innerHTML = nextIcon.innerHTML;
        return el;
      });

      this.lightbox.on("uiRegister", () => {
        this.registerProductsArea();
      });

      this.lightbox.init();
    }

    registerProductsArea() {
      const pswp = this.lightbox.pswp;

      pswp.ui.registerElement({
        name: "lookbook-products",
        order: 9,
        isButton: false,
        appendTo: "root",
        onInit: (el) => {
          this.productsEl = el;
          this.productsEl.className = "pswp__lookbook-products";
          if (this.reservedBottom > 0) {
            this.productsEl.style.setProperty("--lookbook-reserved-bottom", `${this.reservedBottom}px`);
          }
        }
      });

      pswp.on("change", () => this.updateProducts());
      pswp.on("imageSizeChange", () => this.positionProducts());
      pswp.on("zoomPanUpdate", () => this.positionProducts());
      pswp.on("resize", () => this.positionProducts());
    }

    updateProducts() {
      if (!this.productsEl) return;
      const slide = this.lightbox.pswp.currSlide;
      const itemId = slide?.data?.element?.dataset?.itemId;
      const tpl = itemId && document.getElementById(`lookbook-products-${itemId}`);
      this.productsEl.innerHTML = tpl ? tpl.innerHTML : "";
      this.positionProducts();
    }

    positionProducts() {
      if (!this.productsEl) return;
      const pswp = this.lightbox.pswp;
      if (!pswp || !pswp.currSlide) return;
      const slide = pswp.currSlide;
      const w = slide.width || slide.bounds?.center?.width;
      const h = slide.height || slide.bounds?.center?.height;
      if (!w || !h) return;
      const vpW = pswp.viewportSize.x;
      const vpH = pswp.viewportSize.y;
      const right = Math.max(0, (vpW - w) / 2);
      const bottom = Math.max(0, (vpH - h) / 2);
      this.productsEl.style.setProperty("--lookbook-img-right", `${right}px`);
      this.productsEl.style.setProperty("--lookbook-img-bottom", `${bottom}px`);
    }

    subscribeToCartUpdates() {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
        if (data?.source !== "product-card-quick-add") return;
        this.lightbox?.pswp?.close();
      });
    }
  }

  customElements.define("lookbook-gallery", LookbookGallery);
}
